import { LLMProvider, LLMChatRequest, StreamCallbacks, LLMConfig, ToolSchema } from '../../types';

interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: Array<{
    id: string;
    type: 'function';
    function: {
      name: string;
      arguments: string;
    };
  }>;
  tool_call_id?: string;
}

interface OpenAIStreamChunk {
  choices: Array<{
    delta: {
      content?: string;
      tool_calls?: Array<{
        index: number;
        id?: string;
        type?: 'function';
        function?: {
          name?: string;
          arguments?: string;
        };
      }>;
    };
    finish_reason: string | null;
  }>;
}

const convertToOpenAITools = (tools?: ToolSchema[]) => {
  if (!tools || tools.length === 0) return undefined;

  return tools.map((tool) => ({
    type: 'function' as const,
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }));
};

const convertToOpenAIMessages = (request: LLMChatRequest): OpenAIMessage[] => {
  const messages: OpenAIMessage[] = [{ role: 'system', content: request.systemPrompt }];

  for (const msg of request.messages) {
    if (msg.role === 'user') {
      messages.push({ role: 'user', content: msg.content });
    } else if (msg.role === 'assistant') {
      const assistantMsg: OpenAIMessage = { role: 'assistant', content: msg.content || null };
      if (msg.toolCalls && msg.toolCalls.length > 0) {
        assistantMsg.tool_calls = msg.toolCalls.map((tc) => ({
          id: tc.id,
          type: 'function' as const,
          function: {
            name: tc.name,
            arguments: JSON.stringify(tc.arguments),
          },
        }));
      }
      messages.push(assistantMsg);
    } else if (msg.role === 'tool' && msg.toolResult) {
      messages.push({
        role: 'tool',
        content: JSON.stringify(msg.toolResult.result),
        tool_call_id: msg.toolResult.toolCallId,
      });
    }
  }

  return messages;
};

export const createOpenAIProvider = (config: LLMConfig): LLMProvider => {
  let abortController: AbortController | null = null;

  const chat = async (request: LLMChatRequest, callbacks: StreamCallbacks): Promise<void> => {
    abortController = new AbortController();

    try {
      const messages = convertToOpenAIMessages(request);
      const tools = convertToOpenAITools(request.tools);

      const response = await fetch(`${config.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${config.apiKey}`,
        },
        body: JSON.stringify({
          model: config.model,
          messages,
          tools,
          stream: true,
          temperature: config.temperature ?? 0.7,
          max_tokens: config.maxTokens ?? 2048,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI request failed: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';
      const toolCallsInProgress: Map<number, { id: string; name: string; arguments: string }> = new Map();

      while (true) {
        const { done, value } = await reader.read(); // eslint-disable-line no-await-in-loop
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;

          if (trimmed.startsWith('data: ')) {
            try {
              const json: OpenAIStreamChunk = JSON.parse(trimmed.slice(6));
              const delta = json.choices[0]?.delta;

              if (delta?.content) {
                fullContent += delta.content;
                callbacks.onToken(delta.content);
              }

              if (delta?.tool_calls) {
                for (const tc of delta.tool_calls) {
                  if (!toolCallsInProgress.has(tc.index)) {
                    toolCallsInProgress.set(tc.index, {
                      id: tc.id || '',
                      name: tc.function?.name || '',
                      arguments: '',
                    });
                  }

                  const existing = toolCallsInProgress.get(tc.index)!;
                  if (tc.id) existing.id = tc.id;
                  if (tc.function?.name) existing.name = tc.function.name;
                  if (tc.function?.arguments) existing.arguments += tc.function.arguments;
                }
              }

              if (json.choices[0]?.finish_reason === 'tool_calls') {
                for (const [, tc] of toolCallsInProgress) {
                  let args: Record<string, unknown>;
                  try {
                    args = JSON.parse(tc.arguments);
                  } catch {
                    args = {};
                  }

                  callbacks.onToolCall({
                    id: tc.id,
                    name: tc.name,
                    arguments: args,
                  });
                }
                toolCallsInProgress.clear();
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }

      callbacks.onComplete(fullContent);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      callbacks.onError(error as Error);
    } finally {
      abortController = null;
    }
  };

  const cancelRequest = (): void => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return { chat, cancelRequest };
};
